import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getRequest } from 'apis/ApiInstence';
import axiosInstance from 'apis/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';

const AddNews = () => {
  const navigate = useNavigate();
  const baseUrl = useMemo(() => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''), []);
  const backendBase = useMemo(() => (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, ''), []);
  const thumbnailInputRef = useRef(null);

  const [formData, setFormData] = useState({
    news_title: '',
    description: '',
    category: '',
    subcategory: '',
    state: '',
    language: '',
    youtube_url: '',
    reporter_id: '',
    thumbnail: null
  });

  const [repoters, setRepoters] = useState([]);
  const [repotersLoading, setRepotersLoading] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const [dbSubcategories, setDbSubcategories] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fallbackCategories = [
    { value: '/category/desh', label: 'देश' },
    { value: '/category/videsh', label: 'विदेश' },
    { value: '/category/politics', label: 'राजनीति' },
    { value: '/category/cinema', label: 'सिनेमा' },
    { value: '/category/sports', label: 'खेल' },
    { value: '/category/health', label: 'स्वास्थ्य' },
    { value: '/category/dharm', label: 'धर्म' },
    { value: '/category/science-technology', label: 'विज्ञान और प्रौद्योगिकी' },
    { value: '/category/general', label: 'सामान्य' }
  ];

  // Categories loaded from the backend DB so admin always matches the frontend
  const categories =
    Array.isArray(dbCategories) && dbCategories.length > 0
      ? dbCategories
          .filter((cat) => cat && cat.name)
          .map((cat) => ({ value: cat.name, label: cat.name }))
      : fallbackCategories;

  const [states] = useState([
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ]);
  const [languages] = useState([
    { value: 'hindi', label: 'Hindi' },
    { value: 'english', label: 'English' }
  ]);
  const selectedRepoter = repoters.find((item) => String(item.id) === String(formData.reporter_id));

  // Sub-categories of the currently selected category (read from the DB)
  const selectedCategoryId = dbCategories.find((cat) => cat?.name === formData.category)?.id;
  const subcategoriesForCategory = dbSubcategories.filter(
    (sub) => sub && String(sub.category_id) === String(selectedCategoryId)
  );

  useEffect(() => {
    const fetchRepoters = async () => {
      try {
        setRepotersLoading(true);
        const response = await getRequest('repoter');
        setRepoters(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        toast.error('Failed to load repoters list');
      } finally {
        setRepotersLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await getRequest('category');
        setDbCategories(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };

    const fetchSubcategories = async () => {
      try {
        const response = await getRequest('subcategory');
        setDbSubcategories(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load subcategories:', error);
      }
    };

    fetchRepoters();
    fetchCategories();
    fetchSubcategories();
  }, []);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleChange = (e) => {
    const { name, type, value, files } = e.target;

    // FILE INPUT HANDLING
    if (type === "file") {
      const file = files && files[0] ? files[0] : null;

      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));

      // Thumbnail preview handling
      if (name === "thumbnail") {
        setPreview((prevPreview) => {
          if (prevPreview) {
            URL.revokeObjectURL(prevPreview);
          }
          return file ? URL.createObjectURL(file) : null;
        });
      }

      return;
    }

    // NORMAL INPUT HANDLING (reset sub-category when category changes)
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category" ? { subcategory: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const payload = new FormData();
      const categoryLabelByValue = categories.reduce((acc, cat) => {
        acc[cat.value] = cat.label;
        return acc;
      }, {});

      payload.append('news_title', formData.news_title.trim());
      if (formData.description?.trim()) {
        payload.append('description', formData.description.trim());
      }
      payload.append('category', categoryLabelByValue[formData.category] || formData.category);
      if (formData.subcategory) {
        payload.append('subcategory', formData.subcategory);
      }

      if (formData.state) {
        payload.append('state', formData.state);
      }
      if (formData.language) {
        payload.append('language', formData.language);
      }
      if (formData.youtube_url) {
        const youtubeUrl = formData.youtube_url.trim();
        if (youtubeUrl) payload.append('youtube_url', youtubeUrl);
      }

      if (!selectedRepoter) {
        toast.error('Please select a reporter');
        setLoading(false);
        return;
      }
      payload.append('reporter_name', selectedRepoter.name);

      // Automatically send reporter image name if it exists
      if (selectedRepoter.image) {
        payload.append('reporter_image_name', selectedRepoter.image);
      }

      if (formData.thumbnail) {
        payload.append('thumbnail', formData.thumbnail);
      }

      await axiosInstance.post(`${baseUrl}/product`, payload);

      toast.success('✅ News added successfully');

      setFormData({
        news_title: '',
        description: '',
        category: '',
        subcategory: '',
        state: '',
        language: '',
        youtube_url: '',
        reporter_id: '',
        thumbnail: null
      });

      setPreview(null);

      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }

      navigate('/ournews');
    } catch (error) {
      console.error(error);
      const message = error?.response?.data;
      if (typeof message === 'string' && message.toLowerCase().includes('required fields missing')) {
        toast.error('❌ Failed to add news');
      } else {
        toast.error(message || '❌ Failed to add news');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4 mt-md-5 ournews-page">
      {/* ================= PAGE HERO ================= */}
      <div className="news-page-hero mx-1 mx-md-3">
        <div>
          <h4><i className="fa-solid fa-plus-circle mr-2"></i>Add News</h4>
          <p>नई खबर जोड़ें — सभी जानकारी भरें और पब्लिश करें</p>
        </div>
        <button type="button" className="btn-back-list" onClick={() => navigate('/ournews')}>
          <i className="fa-solid fa-arrow-left"></i> News List
        </button>
      </div>

      <form onSubmit={handleSubmit} encType="multipart/form-data" className="mx-1 mx-md-3">
        {/* ================= SECTION 1: BASIC DETAILS ================= */}
        <div className="news-form-card">
          <div className="card-header-bar">
            <span className="section-icon"><i className="fa-solid fa-clipboard-list"></i></span>
            <h6>Basic Details</h6>
            <span className="badge-optional">जरूरी</span>
          </div>
          <div className="card-body-pad">
            <div className="row">
              <div className="col-12">
                <label className="adminlables" htmlFor="news_title">
                  News Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="news_title"
                  name="news_title"
                  className="form-control"
                  placeholder="खबर का शीर्षक लिखें..."
                  value={formData.news_title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-12 col-md-6 mt-4">
                <label className="adminlables" htmlFor="category">
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  className="form-control"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 mt-4">
                <label className="adminlables" htmlFor="subcategory">
                  Sub-Category
                </label>
                <select
                  id="subcategory"
                  name="subcategory"
                  className="form-control"
                  value={formData.subcategory}
                  onChange={handleChange}
                  disabled={!selectedCategoryId || subcategoriesForCategory.length === 0}
                >
                  <option value="">
                    {subcategoriesForCategory.length > 0 ? 'Select Sub-Category' : 'No sub-category'}
                  </option>
                  {subcategoriesForCategory.map((sub) => (
                    <option key={sub.id} value={sub.name}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                <div className="field-hint">
                  {subcategoriesForCategory.length > 0
                    ? 'इस कैटेगरी की उप-श्रेणी चुनें'
                    : 'इस कैटेगरी में कोई उप-श्रेणी नहीं है'}
                </div>
              </div>

              <div className="col-12 col-md-6 mt-4">
                <label className="adminlables" htmlFor="state">
                  State
                </label>
                <select id="state" name="state" className="form-control" value={formData.state} onChange={handleChange}>
                  <option value="">Select State</option>
                  {states.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 mt-4">
                <label className="adminlables" htmlFor="language">
                  Language
                </label>
                <select id="language" name="language" className="form-control" value={formData.language} onChange={handleChange}>
                  <option value="">Select Language</option>
                  {languages.map((lang, i) => (
                    <option key={i} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ================= SECTION 2: CONTENT ================= */}
        <div className="news-form-card">
          <div className="card-header-bar">
            <span className="section-icon"><i className="fa-solid fa-file-lines"></i></span>
            <h6>Content</h6>
          </div>
          <div className="card-body-pad">
            <div className="row">
              <div className="col-12">
                <label className="adminlables" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows="6"
                  placeholder="खबर की पूरी जानकारी यहाँ लिखें..."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 mt-4">
                <label className="adminlables" htmlFor="youtube_url">
                  YouTube Video URL
                </label>
                <input
                  type="url"
                  id="youtube_url"
                  name="youtube_url"
                  className="form-control"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={formData.youtube_url}
                  onChange={handleChange}
                />
                <div className="field-hint">वैकल्पिक — वीडियो वाली खबर के लिए YouTube लिंक डालें</div>
              </div>
            </div>
          </div>
        </div>

        {/* ================= SECTION 3: MEDIA & REPORTER ================= */}
        <div className="news-form-card">
          <div className="card-header-bar">
            <span className="section-icon"><i className="fa-solid fa-images"></i></span>
            <h6>Media & Reporter</h6>
          </div>
          <div className="card-body-pad">
            <div className="row align-items-start">
              <div className="col-12 col-md-6">
                <label className="adminlables" htmlFor="thumbnail">
                  Thumbnail <span className="text-danger">*</span>
                </label>
                <div
                  className="drag-drop-wrapper"
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={(e) =>
                    e.currentTarget.querySelector('.drag-drop-box').classList.add('drag-active')
                  }
                  onDragLeave={(e) =>
                    e.currentTarget.querySelector('.drag-drop-box').classList.remove('drag-active')
                  }
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      handleChange({
                        target: {
                          name: 'thumbnail',
                          type: 'file',
                          files: [file],
                        },
                      });
                    }
                    e.currentTarget.querySelector('.drag-drop-box').classList.remove('drag-active');
                  }}
                >
                  <input
                    type="file"
                    id="thumbnail"
                    name="thumbnail"
                    accept="image/*"
                    className="drag-drop-input"
                    onChange={handleChange}
                    ref={thumbnailInputRef}
                    required
                  />
                  <div className="drag-drop-box">
                    <div className="drag-drop-icon">
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                    </div>
                    <div className="drag-drop-text">Drag & Drop files here</div>
                    <div className="drag-drop-or">or</div>
                    <div className="drag-drop-btn">Browse Files</div>
                  </div>
                  {formData.thumbnail && (
                    <div className="file-name-preview">
                      <i className="fa-solid fa-check-circle mr-1"></i>
                      Selected: {formData.thumbnail.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6 mt-4 mt-md-0">
                <label className="adminlables" htmlFor="reporter_id">
                  Reporter Name <span className="text-danger">*</span>
                </label>
                <select
                  id="reporter_id"
                  name="reporter_id"
                  className="form-control"
                  value={formData.reporter_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{repotersLoading ? 'Loading reporters...' : 'Select Reporter'}</option>
                  {repoters.map((repoter) => (
                    <option key={repoter.id} value={repoter.id}>
                      {repoter.name}
                    </option>
                  ))}
                </select>
                <div className="field-hint">रिपोर्टर को चुनें — नाम और फोटो अपने-आप जुड़ेंगे</div>
              </div>

              {(preview || selectedRepoter?.image) && (
                <div className="col-12 mt-4">
                  <div className="row">
                    {preview && (
                      <div className="col-12 col-md-6 mb-3 mb-md-0">
                        <div className="news-preview-tile">
                          <div className="preview-caption">Thumbnail Preview</div>
                          <img src={preview} alt="preview" className="img-fluid" style={{ maxHeight: 170 }} />
                        </div>
                      </div>
                    )}

                    {selectedRepoter?.image ? (
                      <div className={`col-12 ${preview ? 'col-md-6' : ''}`}>
                        <div className="reporter-preview-row">
                          <img
                            src={`${backendBase}/uploads/repoters/${selectedRepoter.image}`}
                            alt={selectedRepoter.name}
                            width={72}
                            height={72}
                            className="rounded-circle"
                            style={{ objectFit: 'cover' }}
                          />
                          <div className="reporter-meta">
                            <strong>{selectedRepoter.name}</strong>
                            <small>Selected Reporter</small>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ================= SUBMIT BAR ================= */}
        <div className="news-submit-bar">
          <button
            type="button"
            className="btn-reset"
            onClick={() => {
              setFormData({
                news_title: '',
                description: '',
                category: '',
                subcategory: '',
                state: '',
                language: '',
                youtube_url: '',
                reporter_id: '',
                thumbnail: null
              });
              setPreview(null);
              if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
            }}
          >
            <i className="fa-solid fa-rotate-left mr-1"></i> Reset
          </button>
          <button className="btn-news-submit" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane mr-2"></i> Publish News
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddNews;
